/**
 * Every narrowing the server offers, reachable, linkable, and sent as a query parameter
 * (`KLQ-20260821-001` §B) — collapsed into one line of toolbar as of `KLQ-20260910-001`.
 *
 * THREE RULES, AND WHAT EACH ONE COSTS TO BREAK
 *
 * 1. **The URL is the state.** Nothing in this file is component state that also happens to
 *    be written to the address bar: a control builds the next `TaskFilters` and navigates,
 *    the route parses it back, and the register reads what came out. That is what makes a
 *    filtered register a link somebody can paste into a meeting invite, and it is why a
 *    reload lands on the same list rather than on the project-wide one.
 * 2. **The saved filters are the server's answers.** *Mine*, *Overdue*, *Unassigned* and
 *    *This week* go out as `saved=` and are never re-derived here. The server keys *Mine* on
 *    the caller and both date filters on its own clock; a client computing "this week" from a
 *    browser's timezone would disagree with the CSV export of the same filter, which is the
 *    one place the disagreement would be permanent.
 * 3. **A chip carries a number only while its own filter is the active one** (§D). These are
 *    saved-filter chips, not the wireframe's counters: there is no counts endpoint, so a
 *    number beside *Overdue* while some other filter is showing would be a count of rows
 *    nobody asked for. The number that is offered is the loaded count of the list currently
 *    on screen, under `LoadedCount`'s `+` rule.
 *
 * **The shape, since `KLQ-20260910-001`.** This file no longer draws a bar; it fills one in.
 * `components/filter-bar` owns the collapsed row, the panel behind the trigger and the chip
 * per narrowing applied, and it knows nothing about what a filter is — this file supplies the
 * fields, the words and the `summary`. What went with the old layout is the 240px card that
 * sat *inside the scrolling region*: on a laptop it filled the screen so that no task row was
 * visible on first paint, and scrolling to the list scrolled the controls away. The bar now
 * goes in `Screen`'s `toolbar` slot, drawn once by `TasksPage` for all three arrangements
 * instead of twice, once per view, with identical wiring.
 *
 * **Assignee is a set, and *nobody* is one of its members** (`KLQ-20260825-003` §C as revised
 * by `KLQ-20260910-001` §D). Two designs came before this one. They were a single control with
 * a sentinel while the server *ANDed* them, so *"assigned to Ana and to nobody"* was an empty
 * list with no way to see why; the server now ORs them, and 003 §C split them into a picker
 * plus a checkbox beside it. §C was right that the honest reading of *Ana, Martina and nobody*
 * is one question with three answers, and wrong about the conclusion — **three answers to one
 * question is exactly what one `ChipSelect` holds.** So *nobody* is a row in the picker and a
 * chip like any other, `UNASSIGNED` is a sentinel in the control that never reaches the wire,
 * and `?assignee=<uuid>&unassigned=true` still parses to precisely what it always meant.
 *
 * **Four fields hold a set, and each set is held in a `ChipSelect`** (`KLQ-20260825-003` §G):
 * a chip per member, each removable on its own, and a native `<select>` in the same box as the
 * adder. The four *Any …* messages are the pickers' resting text, so an empty field still reads
 * *Any status* and clearing one is taking its chips off. They were four `<select multiple>`s
 * until 2026-09-06, holding the same state and writing the same URL — swapping the control
 * touched no filter logic, which is what §G said it would not.
 *
 * **Every control in the panel is a shared primitive** as of 2026-09-10 — `Select`, `Input`,
 * `ChipSelect`, all at `size="sm"`. Before that one row held three different field systems: a
 * `<span>` label over a `ChipSelect`, `Select`'s own label, and a bare `<input type="date">` on
 * a class this feature's stylesheet had invented — three labels set differently, three heights,
 * and a row that did not line up in any window width.
 */

import { useCallback } from "react";

import type { ReactNode } from "react";

import { ChipSelect, FilterBar, Input, Select } from "@/components";
import { membersQueryOptions } from "@/features/people";
import { useMessages } from "@/lib/i18n";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import type { ChipSelectOption, FilterChip } from "@/components";
import type { TaskKind, TaskPriority, TaskStatus } from "./api";
import { TASK_KINDS, TASK_PRIORITIES, TASK_STATUSES } from "./api";
import type { Narrowing, SavedFilter, TaskFilters, TaskView } from "./filters";
import {
  dayOfDueFrom,
  dayOfDueUntil,
  dueFromDay,
  dueUntilDay,
  narrowings,
  withFieldsCleared,
  withoutNarrowing,
  SAVED_FILTERS,
  searchFromFilters,
  UNASSIGNED,
} from "./filters";
import { KIND_KEY } from "./kinds";
import { LabelPicker } from "./LabelPicker";
import { useProjectLabels } from "./queries";
import { LoadedCount } from "./LoadedCount";
import type { MessageKeys } from "@/lib/i18n";
import styles from "./Tasks.module.css";

type Key = MessageKeys["tasks"];

/**
 * TWIN: `SAVED_FILTERS` in `./filters.ts` and in `domains/tasks/service.py`.
 *
 * Spelled out rather than interpolated because the wire value `this_week` is snake_case and a
 * catalog key with an underscore in it is silently skipped by the i18n gates — their key
 * pattern is `[A-Za-z][A-Za-z0-9.]*`, so a message named after the wire value would never be
 * checked for a dropped argument or an untranslated locale.
 */
const SAVED_KEY: Readonly<Record<SavedFilter, Key>> = {
  mine: "saved.mine",
  overdue: "saved.overdue",
  unassigned: "saved.unassigned",
  needs_an_owner: "saved.needsAnOwner",
  my_company: "saved.myCompany",
  this_week: "saved.thisWeek",
};

const STATUS_KEY: Readonly<Record<TaskStatus, Key>> = {
  open: "status.open",
  in_progress: "status.inProgress",
  done: "status.done",
  closed: "status.closed",
};

const PRIORITY_KEY: Readonly<Record<TaskPriority, Key>> = {
  high: "priority.high",
  normal: "priority.normal",
  low: "priority.low",
};

/** Which word names each three-way field's two answers, by the value it writes. */
const TRI_KEY: Readonly<
  Record<"hasAttachments" | "drawingLinked" | "resolved", readonly [Key, Key]>
> = {
  hasAttachments: ["filter.attachmentsWith", "filter.attachmentsWithout"],
  drawingLinked: ["filter.drawingPinned", "filter.drawingUnpinned"],
  resolved: ["filter.resolvedYes", "filter.resolvedNo"],
};

/** The `<select>` value for a filter that is a three-way question with a nullable answer. */
function triState(value: boolean | null): string {
  return value === null ? "" : value ? "true" : "false";
}

function fromTriState(value: string): boolean | null {
  return value === "" ? null : value === "true";
}

/**
 * What one enum-valued field is holding and what it may still be given, as `ChipSelect` wants
 * them — chips first, then the rest of the enum as the picker's rows.
 *
 * **Both in the field's own order, never the URL's.** `searchFromFilters` sorts the members
 * before they reach the address bar, so chips read out of the filter object in its order would
 * reshuffle themselves the moment somebody pasted the link back: the same filter, drawn
 * differently. `ChipSelect` does not drop the held values from `options` either — its prop doc
 * says the caller does.
 */
function chipsOf<T extends string>(
  all: readonly T[],
  members: readonly T[],
  label: (value: T) => string,
): {
  readonly tokens: readonly ChipSelectOption[];
  readonly options: readonly ChipSelectOption[];
} {
  const held = (value: T) => members.includes(value);
  const chip = (value: T) => ({ id: value, label: label(value) });
  return {
    tokens: all.filter(held).map(chip),
    options: all.filter((value) => !held(value)).map(chip),
  };
}

/** The members after a pick, in the same order — an unknown value is dropped, not defaulted. */
function withPicked<T extends string>(
  all: readonly T[],
  members: readonly T[],
  picked: string,
): readonly T[] {
  return all.filter((value) => members.includes(value) || value === picked);
}

/** The members after a removal. */
function withoutMember<T extends string>(
  members: readonly T[],
  removed: string,
): readonly T[] {
  return members.filter((value) => value !== removed);
}

/**
 * A filter change is a navigation. The one writer of this screen's search params.
 *
 * Exported because the register's *Clear filters* empty state and the board's own bar in the
 * next wave need the same writer — a second one would be a second opinion about what a URL
 * for this screen looks like.
 */
export function useTaskFilterNavigation(
  projectId: string,
  view: TaskView,
): (next: TaskFilters) => void {
  const navigate = useNavigate();
  return useCallback(
    (next: TaskFilters) => {
      void navigate({
        to: "/projects/$projectId/tasks",
        params: { projectId },
        // The filter object is rewritten wholesale — that is what makes the URL the state
        // rather than a copy of it — but the phase rides along untouched. It is the chrome's
        // parameter, not this screen's (`features/tasks/filters.ts`, `TaskSearch.phase`), and
        // clearing a task filter must not silently move the whole project's context.
        search: (previous: { phase?: string }) => ({
          ...searchFromFilters(next, view),
          ...(previous.phase === undefined ? {} : { phase: previous.phase }),
        }),
      });
    },
    [navigate, projectId, view],
  );
}

export interface TaskFilterBarProps {
  readonly projectId: string;
  readonly filters: TaskFilters;
  readonly view: TaskView;
  /**
   * What the list currently on screen is holding, for the active saved chip. `null` where
   * there is no single list to speak for — the board's columns count separately.
   */
  readonly loaded: { readonly count: number; readonly hasMore: boolean } | null;
  /**
   * What acts on the *result* rather than on the question, drawn beside the trigger —
   * `FilterBar`'s own `actions` slot, passed straight through.
   *
   * On this screen it is *Create task*, and only on a phone: above 600px the button belongs in
   * the screen's head where a writer looks for it, and under it the head's row is 32px spent on
   * one button above a list that has none of the screen left. `TasksPage` decides which, so the
   * button exists once.
   */
  readonly actions?: ReactNode;
}

export function TaskFilterBar({
  projectId,
  filters,
  view,
  loaded,
  actions,
}: TaskFilterBarProps) {
  const t = useMessages("tasks");
  const apply = useTaskFilterNavigation(projectId, view);
  // Names for the assignee picker. `read_people` has an `architect` floor while
  // `Surface.tasks` grants an engineer and a contractor write, so for those two roles this
  // call answers `403` and the picker has no names to offer (§G). It renders empty rather
  // than absent: a filter the URL already carries stays applied and stays visible.
  const members = useQuery(membersQueryOptions(projectId));
  const named = members.data?.items ?? [];
  const nameOf = (member: (typeof named)[number]) =>
    member.display_name ?? member.email ?? t("person.unavailable");

  const kind = chipsOf(TASK_KINDS, filters.kind, (value) => t(KIND_KEY[value]));
  const status = chipsOf(TASK_STATUSES, filters.status, (value) =>
    t(STATUS_KEY[value]),
  );
  const priority = chipsOf(TASK_PRIORITIES, filters.priority, (value) =>
    t(PRIORITY_KEY[value]),
  );

  const labels = useProjectLabelNames(projectId);

  /**
   * The assignee set's chips: *nobody* first, then the people this caller can name, then the
   * ids they cannot.
   *
   * An id with no name behind it — a member list they may not read (`403`), or a person who
   * has left — keeps a chip reading *name unavailable* and stays removable: the filter is the
   * server's either way, and a control that hid it would lie about what is applied.
   *
   * *Nobody* leads because it is the answer people reach for, and because a chip that appeared
   * at the end of a list of names would read as a person called *Unassigned*.
   */
  const assigneeTokens: readonly ChipSelectOption[] = [
    ...(filters.unassigned
      ? [{ id: UNASSIGNED, label: t("filter.assigneeUnassigned") }]
      : []),
    ...named
      .filter((member) => filters.assignee.includes(member.user_id))
      .map((member) => ({ id: member.user_id, label: nameOf(member) })),
    ...filters.assignee
      .filter((userId) => !named.some((member) => member.user_id === userId))
      .map((userId) => ({ id: userId, label: t("person.unavailable") })),
  ];
  const assigneeOptions: readonly ChipSelectOption[] = [
    ...(filters.unassigned
      ? []
      : [{ id: UNASSIGNED, label: t("filter.assigneeUnassigned") }]),
    ...named
      .filter((member) => !filters.assignee.includes(member.user_id))
      .map((member) => ({ id: member.user_id, label: nameOf(member) })),
  ];

  /**
   * What the bar draws above the list: one chip per narrowing, in the words the person picked
   * them by.
   *
   * `narrowings` decides *what* is applied and `withoutNarrowing` decides what taking one off
   * means — both pure, both in `filters.ts`, both under vitest. This function only puts words
   * on them, which is the split that keeps the chip row from becoming a third opinion about
   * what a filter is.
   */
  const label = (narrowing: Narrowing): string => {
    const { field, value } = narrowing;
    if (field === "kind") return t(KIND_KEY[value as TaskKind]);
    if (field === "status") return t(STATUS_KEY[value as TaskStatus]);
    if (field === "priority") return t(PRIORITY_KEY[value as TaskPriority]);
    if (field === "assignee") {
      if (value === UNASSIGNED) return t("filter.assigneeUnassigned");
      const member = named.find((one) => one.user_id === value);
      return member === undefined ? t("person.unavailable") : nameOf(member);
    }
    if (field === "labels") return labels(value);
    // A date bound reads as the day it holds, prefixed by which bound it is: two chips both
    // saying `30. 9. 2026` would be two filters a person cannot tell apart.
    if (field === "dueAfter") return `${t("filter.dueFrom")}: ${value}`;
    if (field === "dueBefore") return `${t("filter.dueUntil")}: ${value}`;
    const pair = TRI_KEY[field as keyof typeof TRI_KEY];
    return t(pair[value === "true" ? 0 : 1]);
  };

  const summary: readonly FilterChip[] = narrowings(filters).map((narrowing) => ({
    id: `${narrowing.field}:${narrowing.value}`,
    field: narrowing.field,
    label: label(narrowing),
    onRemove: () => {
      apply(withoutNarrowing(filters, narrowing));
    },
  }));

  return (
    <FilterBar
      legend={t("filter.legend")}
      triggerLabel={t("filter.open")}
      panelLabel={t("filter.legend")}
      closeLabel={t("filter.close")}
      actions={actions}
      summary={summary}
      clearLabel={t("filter.clear")}
      removeLabel={(name) => t("filter.remove", { name })}
      onClear={() => {
        // The bar's own controls only. A slice picked in the rail is not cleared here — the
        // bar does not show it, and a button that undoes something invisible is a button
        // people learn not to press (`withFieldsCleared`).
        apply(withFieldsCleared(filters));
      }}
      saved={
        <div className={styles.saved} role="group" aria-label={t("saved.legend")}>
          {SAVED_FILTERS.map((saved) => {
            const active = filters.saved === saved;
            return (
              <button
                key={saved}
                type="button"
                aria-pressed={active}
                className={[styles.chip, active ? styles.chipOn : ""]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  // Clicking the active chip clears it: these are one question with four
                  // answers plus "none of them", not four independent switches.
                  apply({ ...filters, saved: active ? null : saved });
                }}
              >
                {t(SAVED_KEY[saved])}
                {active && loaded !== null ? (
                  <LoadedCount
                    count={loaded.count}
                    hasMore={loaded.hasMore}
                    bare
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      }
    >
      {/* A `<div>` rather than a `<label>`, for all four sets: a `ChipSelect` holds several
          controls, and a label wrapping them names the first one it finds — a chip's cross —
          rather than the field. Each picker carries its own *Any …* copy as its name. */}
      <div className={styles.panelField}>
        <span className={styles.panelLabel}>{t("filter.kind")}</span>
        <ChipSelect
          size="sm"
          tokens={kind.tokens}
          options={kind.options}
          addLabel={t("filter.kindAny")}
          removeLabel={(name) => t("filter.remove", { name })}
          onAdd={(picked) => {
            apply({
              ...filters,
              kind: withPicked(TASK_KINDS, filters.kind, picked),
            });
          }}
          onRemove={(removed) => {
            apply({ ...filters, kind: withoutMember(filters.kind, removed) });
          }}
        />
      </div>

      <div className={styles.panelField}>
        <span className={styles.panelLabel}>{t("filter.status")}</span>
        <ChipSelect
          size="sm"
          tokens={status.tokens}
          options={status.options}
          addLabel={t("filter.statusAny")}
          removeLabel={(name) => t("filter.remove", { name })}
          onAdd={(picked) => {
            apply({
              ...filters,
              status: withPicked(TASK_STATUSES, filters.status, picked),
            });
          }}
          onRemove={(removed) => {
            apply({
              ...filters,
              status: withoutMember(filters.status, removed),
            });
          }}
        />
      </div>

      <div className={styles.panelField}>
        <span className={styles.panelLabel}>{t("filter.priority")}</span>
        <ChipSelect
          size="sm"
          tokens={priority.tokens}
          options={priority.options}
          addLabel={t("filter.priorityAny")}
          removeLabel={(name) => t("filter.remove", { name })}
          onAdd={(picked) => {
            apply({
              ...filters,
              priority: withPicked(TASK_PRIORITIES, filters.priority, picked),
            });
          }}
          onRemove={(removed) => {
            apply({
              ...filters,
              priority: withoutMember(filters.priority, removed),
            });
          }}
        />
      </div>

      {/* Wide: a set of people wraps to a second row of chips sooner than any other field,
          and a two-column panel that put it in one column would wrap it on the first name. */}
      <div className={[styles.panelField, "wide"].join(" ")}>
        <span className={styles.panelLabel}>{t("filter.assignee")}</span>
        <ChipSelect
          size="sm"
          tokens={assigneeTokens}
          options={assigneeOptions}
          addLabel={t("filter.assigneeAny")}
          removeLabel={(name) => t("filter.remove", { name })}
          onAdd={(picked) => {
            // *Nobody* is the one member of this set that is not a user id (§D). It flips the
            // flag the server ORs in; everything else joins the list it ANDs the flag with.
            apply(
              picked === UNASSIGNED
                ? { ...filters, unassigned: true }
                : { ...filters, assignee: [...filters.assignee, picked] },
            );
          }}
          onRemove={(removed) => {
            apply(withoutNarrowing(filters, { field: "assignee", value: removed }));
          }}
        />
      </div>

      <div className={styles.panelField}>
        <Input
          type="date"
          size="sm"
          label={t("filter.dueFrom")}
          value={dayOfDueFrom(filters.dueAfter)}
          onChange={(event) => {
            apply({ ...filters, dueAfter: dueFromDay(event.target.value) });
          }}
        />
      </div>

      <div className={styles.panelField}>
        <Input
          type="date"
          size="sm"
          label={t("filter.dueUntil")}
          value={dayOfDueUntil(filters.dueBefore)}
          onChange={(event) => {
            apply({ ...filters, dueBefore: dueUntilDay(event.target.value) });
          }}
        />
      </div>

      <div className={styles.panelField}>
        <Select
          label={t("filter.attachments")}
          size="sm"
          value={triState(filters.hasAttachments)}
          onChange={(event) => {
            apply({
              ...filters,
              hasAttachments: fromTriState(event.target.value),
            });
          }}
        >
          <option value="">{t("filter.any")}</option>
          <option value="true">{t("filter.attachmentsWith")}</option>
          <option value="false">{t("filter.attachmentsWithout")}</option>
        </Select>
      </div>

      <div className={styles.panelField}>
        <Select
          label={t("filter.drawing")}
          size="sm"
          value={triState(filters.drawingLinked)}
          onChange={(event) => {
            apply({
              ...filters,
              drawingLinked: fromTriState(event.target.value),
            });
          }}
        >
          <option value="">{t("filter.any")}</option>
          <option value="true">{t("filter.drawingPinned")}</option>
          <option value="false">{t("filter.drawingUnpinned")}</option>
        </Select>
      </div>

      <div className={styles.panelField}>
        <Select
          label={t("filter.resolved")}
          size="sm"
          value={triState(filters.resolved)}
          onChange={(event) => {
            apply({ ...filters, resolved: fromTriState(event.target.value) });
          }}
        >
          <option value="">{t("filter.any")}</option>
          <option value="true">{t("filter.resolvedYes")}</option>
          <option value="false">{t("filter.resolvedNo")}</option>
        </Select>
      </div>

      <div className={[styles.panelField, "wide"].join(" ")}>
        <LabelPicker
          projectId={projectId}
          labels={filters.labels}
          onChange={(next) => {
            apply({ ...filters, labels: next });
          }}
        />
      </div>
    </FilterBar>
  );
}

/**
 * A label id, as its name — for the chip row, which draws a narrowing the panel is not open to
 * explain.
 *
 * The project's labels are already on this screen (`LabelPicker` reads them, the register
 * renders them per row), so this is the cached list read a third time rather than a request.
 * An id with no label behind it — one deleted while somebody's link still names it — reads as
 * its own id rather than disappearing: the filter is applied and the chip must say so.
 */
function useProjectLabelNames(projectId: string): (id: string) => string {
  const labels = useProjectLabels(projectId);
  const items = labels.data?.items ?? [];
  return (id) => items.find((one) => one.id === id)?.name ?? id;
}
